/**
 * "connect": two columns; drag from an element on one side to its matching
 * element on the other to draw a line (PlayspaceConnect.cs /
 * ConnectElementPlayer.cs). Either side can start the drag. Items = connect
 * elements; a wrong connection counts an error and the line disappears.
 * A container can take several elements (ConnectElementPlayer.IsCorrect only
 * retires the connected element): it is done once all of its elements are.
 */
import { useMemo, useRef, useState } from "react";
import type { Element as El, SlideOf } from "@makeit/schema";
import { ElementCard } from "../elements";
import { useActivity } from "../activity";
import { toStagePoint, useDraggable, useDropZone, useStageScale } from "../drag";
import { usePlayer } from "../context";
import { shuffled } from "../util";
import { useFx } from "../fx";

interface Pt { x: number; y: number }

export function ConnectSlide({ slide }: { slide: SlideOf<"connect"> }) {
  const { project } = usePlayer();
  const random = project.settings.randomOrderElements;
  const left = useMemo(() => (random ? shuffled(slide.connectElements) : slide.connectElements), [slide, random]);
  const right = useMemo(() => (random ? shuffled(slide.containerElements) : slide.containerElements), [slide, random]);
  const activity = useActivity(Math.max(1, left.length));
  const rootRef = useRef<HTMLDivElement>(null);
  const scale = useStageScale();
  const [lines, setLines] = useState<{ a: Pt; b: Pt }[]>([]);
  const [connected, setConnected] = useState<Set<string>>(new Set()); // connect element ids
  const containerDone = (id: string) => left.every((e) => e.containerId !== id || connected.has(e.id));
  const [live, setLive] = useState<{ a: Pt; b: Pt } | null>(null);
  const fx = useFx();

  const centerOf = (id: string): Pt | null => {
    const el = rootRef.current?.querySelector<HTMLElement>(`[data-node="${id}"]`);
    if (!el || !rootRef.current) return null;
    const r = el.getBoundingClientRect();
    return toStagePoint(rootRef.current, r.left + r.width / 2, r.top + r.height / 2, scale);
  };

  const tryConnect = (fromId: string, toZone: string | null) => {
    setLive(null);
    if (!toZone) return false;
    const toId = toZone.replace("n:", "");
    const l = left.find((e) => e.id === fromId) ?? left.find((e) => e.id === toId);
    const r = right.find((e) => e.id === toId) ?? right.find((e) => e.id === fromId);
    if (!l || !r || connected.has(l.id)) return false;
    if (l.containerId === r.id) {
      const a = centerOf(l.id);
      const b = centerOf(r.id);
      if (a && b) setLines((ls) => [...ls, { a, b }]);
      setConnected((c) => new Set([...c, l.id]));
      fx.stars(rootRef.current?.querySelector<HTMLElement>(`[data-node="${toId}"]`));
      activity.correct(l.text.content);
    } else {
      activity.wrong(true, l.text.content);
    }
    return false; // never keep the dragged element displaced
  };

  const onMove = (fromId: string, client: Pt) => {
    const a = centerOf(fromId);
    if (!a || !rootRef.current) return;
    setLive({ a, b: toStagePoint(rootRef.current, client.x, client.y, scale) });
  };

  return (
    <div className="connect-activity" ref={rootRef}>
      <svg className="connect-lines" width="2048" height="1536">
        {lines.map((l, i) => (
          <line key={i} x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y} className="line-ok" />
        ))}
        {live && <line x1={live.a.x} y1={live.a.y} x2={live.b.x} y2={live.b.y} className="line-live" />}
      </svg>
      <div className="connect-col left">
        {left.map((e) => (
          <Node key={e.id} element={e} done={connected.has(e.id)} onDrop={(z) => tryConnect(e.id, z)} onMove={(p) => onMove(e.id, p)} />
        ))}
      </div>
      <div className="connect-col right">
        {right.map((e) => (
          <Node key={e.id} element={e} done={containerDone(e.id)} onDrop={(z) => tryConnect(e.id, z)} onMove={(p) => onMove(e.id, p)} />
        ))}
      </div>
    </div>
  );
}

function Node({ element, done, onDrop, onMove }: { element: El; done: boolean; onDrop: (zone: string | null) => boolean; onMove: (p: Pt) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useDropZone(`n:${element.id}`, ref, !done);
  const drag = useDraggable({ onDrop, onMove, disabled: done });
  return (
    <div ref={ref} data-node={element.id} className={`connect-node ${done ? "done" : ""}`} style={{ cursor: drag.style.cursor, touchAction: "none" }} {...drag.handlers}>
      <ElementCard element={element} className="connect-card" bg="item" fit={false} />
    </div>
  );
}
